import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaHandsHelping,
  FaMoneyBill,
  FaPlus,
  FaShare,
  FaStar,
  FaDownload,
  FaCheck,
} from 'react-icons/fa';
import { FaXmark } from 'react-icons/fa6';
import { IoGiftSharp } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { PropsOf } from '@headlessui/react/dist/types';
import { twMerge } from 'tailwind-merge';
import {
  getAffiliateLeaderboard,
  getCurrentAffiliate,
  getReferredCommunities,
} from '../../services/affiliateService';
import useSelectedBusiness from '../../hooks/business/useSelectedBusiness';
import PageHeader from '../components/misc/PageHeader';
import PageWithTabsLayout from '../components/layouts/PageWithTabsLayout';
import EmptyScreenView from '../components/misc/EmptyScreenView';
import AffiliateCalculator from '../components/misc/AffiliateCalculator';
import PrimaryButton from '../components/buttons/PrimaryButton';
import ShareAffiliateLinkModal from '../components/modals/ShareAffiliateLinkModal';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
  BaseTableSpacerRow,
} from '../components/tables/BaseTable';
import Button from '../components/buttons/Button';
import {
  Affiliate,
  AffiliateCodeBusiness,
  AffiliateLeaderboardEntry,
} from '../../services/model/Affiliate.model';

export enum AffiliatePageTab {
  INFO = 'info',
  INSIGHTS = 'insights',
  LEADERBOARD = 'leaderboard',
  //PAYOUTS = 'payouts', commented out for now
}

export default function GeneralAffiliatePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const business = useSelectedBusiness();
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = React.useState<AffiliatePageTab>(AffiliatePageTab.INFO);

  const [affiliate, setAffiliate] = useState<Affiliate | undefined>(undefined);

  useEffect(() => {
    const fetchAffiliate = async () => {
      try {
        const affiliateData = await getCurrentAffiliate();
        if (affiliateData) {
          setAffiliate(affiliateData);
          setActiveTab(AffiliatePageTab.INSIGHTS);
        } else {
          setAffiliate(undefined);
          setActiveTab(AffiliatePageTab.INFO);
        }
      } catch (error) {
        console.error('Failed to fetch affiliate data:', error);
      }
    };
    fetchAffiliate();
  }, [business?.businessId]);

  const renderTabContent = (tab: AffiliatePageTab): JSX.Element => {
    switch (tab) {
      case AffiliatePageTab.INFO:
        return <AffiliateInfoTab />;
      case AffiliatePageTab.INSIGHTS:
        return affiliate ? <AffiliateInsightsTab {...affiliate} /> : <EmptyState />;
      case AffiliatePageTab.LEADERBOARD:
        return <AffiliateLeaderboardTab />;
      //case AffiliatePageTab.PAYOUTS:
      //  return affiliate ? <AffiliatePayoutsTab /> : <EmptyState />;
      default:
        return <></>;
    }
  };

  return (
    <>
      <div>
        <PageHeader
          title={t('page.affiliate.title')}
          subtitle={t('page.affiliate.subtitle')}
          primaryButton={
            affiliate ? (
              <PrimaryButton
                text={t('page.affiliate.share')}
                icon={FaShare}
                onClick={() => setModalOpen(true)}
              />
            ) : (
              <PrimaryButton
                text={t('general.create')}
                icon={FaPlus}
                onClick={() => navigate('/affiliate/create')}
              />
            )
          }
        />

        <PageWithTabsLayout
          tabs={Object.values(AffiliatePageTab)}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          renderTabContent={renderTabContent}
        />
      </div>

      <ShareAffiliateLinkModal
        open={modalOpen}
        setOpen={setModalOpen}
        affiliateCode={affiliate ? affiliate.code : ''}
      />
    </>
  );
}

function AffiliateInfoTab(): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex w-full flex-col gap-8">
      <div className="rounded-xl bg-gradient-to-br from-primary-300 to-primary-300/25 p-8 text-secondary-50">
        <div className="mx-auto">
          <h2 className="mb-4 text-3xl font-bold">{t('page.affiliate.info.title')}</h2>
          <p className="mb-6 text-lg opacity-90">{t('page.affiliate.info.description')}</p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-lg bg-white bg-opacity-10 p-4 backdrop-blur-sm">
              <h3 className="mb-2 text-lg font-semibold">
                {t('page.affiliate.info.commissionTitle')}
              </h3>
              <p className="text-sm opacity-80">{t('page.affiliate.info.commissionDescription')}</p>
            </div>
            <div className="rounded-lg bg-white bg-opacity-10 p-4 backdrop-blur-sm">
              <h3 className="mb-2 text-lg font-semibold">
                {t('page.affiliate.info.durationTitle')}
              </h3>
              <p className="text-sm opacity-80">{t('page.affiliate.info.durationDescription')}</p>
            </div>
            <div className="rounded-lg bg-white bg-opacity-10 p-4 backdrop-blur-sm">
              <h3 className="mb-2 text-lg font-semibold">{t('page.affiliate.info.payoutTitle')}</h3>
              <p className="text-sm opacity-80">{t('page.affiliate.info.payoutDescription')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <div className="text-center">
          <h2 className="mb-2 text-2xl font-semibold text-primary-900">
            {t('page.affiliate.info.earnTitle')}
          </h2>
          <p className="mx-auto max-w-3xl text-gray-600">
            {t('page.affiliate.info.earnDescription')}
          </p>
        </div>
        <div className="flex justify-center">
          <AffiliateCalculator />
        </div>
      </div>

      <div className="rounded-xl bg-secondary-200 bg-opacity-50 p-8">
        <div className="mx-auto">
          <h2 className="mb-6 text-center text-2xl font-semibold text-primary-900">
            {t('page.affiliate.info.whyParticipateTitle')}
          </h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="rounded-lg bg-white p-6 text-center shadow-sm">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-300">
                <FaMoneyBill className="text-2xl text-secondary-50" />
              </div>
              <h3 className="mb-3 text-lg font-semibold text-primary-900">
                {t('page.affiliate.info.earnForFreeTitle')}
              </h3>
              <p className="text-gray-600">{t('page.affiliate.info.earnForFreeDescription')}</p>
            </div>
            <div className="rounded-lg bg-white p-6 text-center shadow-sm">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-300">
                <FaHandsHelping className="text-2xl text-secondary-50" />
              </div>
              <h3 className="mb-3 text-lg font-semibold text-primary-900">
                {t('page.affiliate.info.helpOthersTitle')}
              </h3>
              <p className="text-gray-600">{t('page.affiliate.info.helpOthersDescription')}</p>
            </div>
            <div className="rounded-lg bg-white p-6 text-center shadow-sm">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-300">
                <FaStar className="text-2xl text-secondary-50" />
              </div>
              <h3 className="mb-3 text-lg font-semibold text-primary-900">
                {t('page.affiliate.info.extraBonusesTitle')}
              </h3>
              <p className="text-gray-600">{t('page.affiliate.info.extraBonusesDescription')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto">
        <h2 className="mb-6 text-center text-2xl font-semibold text-primary-900">
          {t('page.affiliate.info.extraBenefitsTitle')}
        </h2>
        <p className="mx-auto mb-8 max-w-3xl text-center text-gray-600">
          {t('page.affiliate.info.extraBenefitsDescription')}
        </p>
        <div className="mx-auto max-w-2xl rounded-xl bg-secondary-200 bg-opacity-50 p-8 shadow-sm">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-primary-300 to-primary-300/15">
              <IoGiftSharp className="text-3xl" />
            </div>
            <h3 className="mb-2 text-3xl font-bold text-primary-900">
              {t('page.affiliate.info.bonusTitle')}
            </h3>
            <p className="mb-6 text-xl text-gray-700">
              {t('page.affiliate.info.bonusDescription')}
            </p>
            <p className="mb-8 text-gray-600">{t('page.affiliate.info.bonusDescription')}</p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-lg bg-white p-4">
                <h4 className="mb-2 font-semibold text-primary-900">
                  {t('page.affiliate.info.bonusGroupTitle')}
                </h4>
                <p className="text-sm text-gray-600">
                  {t('page.affiliate.info.bonusGroupDescription')}
                </p>
              </div>
              <div className="rounded-lg bg-white p-4">
                <h4 className="mb-2 font-semibold text-primary-900">
                  {t('page.affiliate.info.bonusYouTitle')}
                </h4>
                <p className="text-sm text-gray-600">
                  {t('page.affiliate.info.bonusYouDescription')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl bg-secondary-200 bg-opacity-50 p-8">
        <div className="mx-auto">
          <h2 className="mb-6 text-center text-2xl font-semibold text-primary-900">
            {t('page.affiliate.info.howItWorksTitle')}
          </h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-300">
                <span className="text-2xl font-bold text-secondary-50">1</span>
              </div>
              <h3 className="mb-2 text-lg font-semibold">{t('page.affiliate.info.step1Title')}</h3>
              <p className="text-sm text-gray-600">{t('page.affiliate.info.step1Description')}</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-300">
                <span className="text-2xl font-bold text-secondary-50">2</span>
              </div>
              <h3 className="mb-2 text-lg font-semibold">{t('page.affiliate.info.step2Title')}</h3>
              <p className="text-sm text-gray-600">{t('page.affiliate.info.step2Description')}</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-300">
                <span className="text-2xl font-bold text-secondary-50">3</span>
              </div>
              <h3 className="mb-2 text-lg font-semibold">{t('page.affiliate.info.step3Title')}</h3>
              <p className="text-sm text-gray-600">{t('page.affiliate.info.step3Description')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl bg-secondary-200 bg-opacity-50 p-8">
        <div className="mx-auto">
          <h2 className="mb-6 text-center text-2xl font-semibold text-primary-900">
            {t('page.affiliate.info.importantInfoTitle')}
          </h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="rounded-lg bg-white p-6 shadow-sm">
              <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-primary-900">
                {t('page.affiliate.info.paymentTitle')}
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <FaCheck className="mt-1" />
                  <span className="text-gray-700">
                    {t('page.affiliate.info.paymentDescription1')}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <FaCheck className="mt-1" />
                  <span className="text-gray-700">
                    {t('page.affiliate.info.paymentDescription2')}
                  </span>
                </li>
              </ul>
            </div>
            <div className="rounded-lg bg-white p-6 shadow-sm">
              <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-primary-900">
                {t('page.affiliate.info.preventionTitle')}
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <FaXmark className="mt-1" />
                  <span className="text-gray-700">
                    {t('page.affiliate.info.preventionDescription1')}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AffiliateInsightsTab({ id }: Affiliate): JSX.Element {
  const { t } = useTranslation();
  const [referredCommunities, setReferredCommunities] = useState<AffiliateCodeBusiness[]>([]);

  useEffect(() => {
    const fetchReferredCommunities = async () => {
      try {
        const communities = await getReferredCommunities(id);
        setReferredCommunities(communities);
      } catch (error) {
        console.error('Failed to fetch referred communities:', error);
      }
    };
    fetchReferredCommunities();
  }, [id]);

  function getCommunityStatus(community: AffiliateCodeBusiness): string {
    if (community.business == null) return 'deleted';
    if (community.expiresAt < new Date()) return 'expired';
    return 'active';
  }

  const totalEarned = referredCommunities.reduce(
    (sum, community) => sum + community.earned / 100,
    0,
  );
  const activeCommunitiesCount = referredCommunities.filter(
    (c) => getCommunityStatus(c) === 'active',
  ).length;

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-lg bg-secondary-200 bg-opacity-50 p-6">
          <h3 className="mb-2 text-lg font-semibold text-primary-900">
            {t('page.affiliate.insights.totalCommunities')}
          </h3>
          <p className="text-primary-600 text-2xl font-bold">{referredCommunities.length}</p>
        </div>
        <div className="rounded-lg bg-secondary-200 bg-opacity-50 p-6">
          <h3 className="mb-2 text-lg font-semibold text-primary-900">
            {t('page.affiliate.insights.activeCommunities')}
          </h3>
          <p className="text-primary-600 text-2xl font-bold">{activeCommunitiesCount}</p>
        </div>
        <div className="rounded-lg bg-secondary-200 bg-opacity-50 p-6">
          <h3 className="mb-2 text-lg font-semibold text-primary-900">
            {t('page.affiliate.insights.totalEarned')}
          </h3>
          <p className="text-primary-600 text-2xl font-bold">€{totalEarned.toFixed(2)}</p>
        </div>
      </div>

      <BaseTable>
        <BaseTableHeader>
          <BaseTableHeaderRow>
            <HeaderCell>{t('page.affiliate.insights.community')}</HeaderCell>
            <HeaderCell>{t('page.affiliate.insights.status')}</HeaderCell>
            <HeaderCell>{t('page.affiliate.insights.earned')}</HeaderCell>
            <HeaderCell>{t('page.affiliate.insights.expireDate')}</HeaderCell>
          </BaseTableHeaderRow>
        </BaseTableHeader>
        <BaseTableBody>
          <BaseTableSpacerRow />
          {referredCommunities.map((community) => {
            const communityStatus: string = getCommunityStatus(community);
            return (
              <BaseTableDataRow
                key={community.business ? community.business?.businessId : 'deleted'}>
                <DataCell>
                  {community.business
                    ? community.business.name
                    : t('page.affiliate.insights.deletedCommunity')}
                </DataCell>
                <DataCell>
                  <span
                    className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                      communityStatus === 'active'
                        ? 'bg-green-100 text-success'
                        : communityStatus === 'deleted'
                          ? 'bg-red-100 text-danger'
                          : 'bg-gray-100 text-gray-800'
                    }`}>
                    {t(
                      `page.affiliate.insights.status${communityStatus.charAt(0).toUpperCase() + communityStatus.slice(1)}`,
                    )}
                  </span>
                </DataCell>
                <DataCell>€{(community.earned / 100).toFixed(2)}</DataCell>
                <DataCell>{new Date(community.expiresAt).toLocaleDateString()}</DataCell>
              </BaseTableDataRow>
            );
          })}
        </BaseTableBody>
      </BaseTable>
    </div>
  );
}

function AffiliateLeaderboardTab(): JSX.Element {
  const { t } = useTranslation();
  const [leaderboardData, setLeaderboardData] = useState<AffiliateLeaderboardEntry[]>([]);

  const pointBreakdown = [
    { action: t('page.affiliate.leaderboard.actions.communityWith5Users'), points: 10 },
    { action: t('page.affiliate.leaderboard.actions.newUserJoins'), points: 3 },
    { action: t('page.affiliate.leaderboard.actions.existingUserJoins'), points: 1 },
  ];

  useEffect(() => {
    const fetchReferredCommunities = async () => {
      try {
        const entries = await getAffiliateLeaderboard();
        setLeaderboardData(entries);
      } catch (error) {
        console.error('Failed to fetch leaderboard:', error);
      }
    };
    fetchReferredCommunities();
  }, []);

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="rounded-xl bg-secondary-200 bg-opacity-50 p-6">
        <h2 className="mb-3 text-2xl font-semibold text-primary-900">
          {t('page.affiliate.leaderboard.howToEarnPoints')}
        </h2>
        <p className="mb-4 text-gray-600">{t('page.affiliate.leaderboard.pointsExplanation')}</p>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {pointBreakdown.map((item, index) => (
            <div key={index} className="rounded-lg bg-white p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-primary-900">{item.action}</h3>
                <span className="text-primary-600 font-bold">
                  +{item.points}{' '}
                  {item.points > 1
                    ? t('page.affiliate.leaderboard.points')
                    : t('page.affiliate.leaderboard.point')}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Prize section - commented out for now */}
      {/* <div className="rounded-xl bg-gradient-to-br from-primary-300 to-primary-300/15 p-8 text-secondary-50 flex items-center gap-6">
        <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center">
          <img src="https://png.pngtree.com/png-vector/20240202/ourmid/pngtree-bicycle-png-illustration-png-image_11586285.png" alt={t('page.affiliate.leaderboard.prizeAlt')} className="w-16 h-16" />
        </div>
        <div className="flex flex-col">
          <h2 className="text-3xl font-bold mb-4">{t('page.affiliate.leaderboard.prizeTitle')}</h2>
          <p className="text-lg mb-6 opacity-90">{t('page.affiliate.leaderboard.prizeDescription')}</p>
        </div>
      </div> */}

      {leaderboardData.length === 0 ? (
        <EmptyScreenView />
      ) : (
        <BaseTable>
          <BaseTableHeader>
            <BaseTableHeaderRow>
              <HeaderCell>{t('page.affiliate.leaderboard.communityName')}</HeaderCell>
              <HeaderCell>{t('page.affiliate.leaderboard.pointsThisMonth')}</HeaderCell>
              <HeaderCell>{t('page.affiliate.leaderboard.pointsThisYear')}</HeaderCell>
            </BaseTableHeaderRow>
          </BaseTableHeader>
          <BaseTableBody>
            <BaseTableSpacerRow />
            {leaderboardData.map((community) => (
              <BaseTableDataRow key={community.businessName}>
                <DataCell>{community.businessName}</DataCell>
                <DataCell>{community.monthlyEarned}</DataCell>
                <DataCell>{community.totalEarned}</DataCell>
              </BaseTableDataRow>
            ))}
          </BaseTableBody>
        </BaseTable>
      )}
    </div>
  );
}

function AffiliatePayoutsTab(): JSX.Element {
  const { t } = useTranslation();
  //TODO - Affiliate: API implementation
  const payouts = [
    { date: '2025-06-15', amount: 150.0 },
    { date: '2025-05-15', amount: 200.0 },
    { date: '2025-04-15', amount: 175.0 },
    { date: '2025-03-15', amount: 225.0 },
  ];

  const downloadInvoice = () => {
    //TODO - Affiliate: Implement invoice download logic
  };

  return (
    <div className="flex w-full flex-col gap-6">
      <BaseTable>
        <BaseTableHeader>
          <BaseTableHeaderRow>
            <HeaderCell>{t('page.affiliate.payouts.payoutDate')}</HeaderCell>
            <HeaderCell>{t('page.affiliate.payouts.amount')}</HeaderCell>
            <HeaderCell />
          </BaseTableHeaderRow>
        </BaseTableHeader>
        <BaseTableBody>
          <BaseTableSpacerRow />
          {payouts.map((payout, index) => (
            <BaseTableDataRow key={index}>
              <DataCell>{new Date(payout.date).toLocaleDateString()}</DataCell>
              <DataCell>€{payout.amount.toFixed(2)}</DataCell>
              <DataCell>
                <Button variant="secondary" onClick={downloadInvoice} className="rounded-full p-3">
                  <FaDownload />
                </Button>
              </DataCell>
            </BaseTableDataRow>
          ))}
        </BaseTableBody>
      </BaseTable>
    </div>
  );
}

function HeaderCell({ className, ...props }: PropsOf<'th'>): JSX.Element {
  return <th className={twMerge('py-3 pl-1 text-primary-900', className)} {...props} />;
}

function DataCell({ className, ...props }: PropsOf<'td'>): JSX.Element {
  return (
    <td
      className={twMerge(
        'whitespace-nowrap py-4 pl-2 text-sm font-medium text-primary-900',
        className,
      )}
      {...props}
    />
  );
}

function EmptyState(): JSX.Element {
  const { t } = useTranslation();

  return (
    <>
      <EmptyScreenView
        title={t('page.affiliate.activateTitle')}
        subtitle={t('page.affiliate.activateDescription')}
      />
    </>
  );
}
